import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { seminarApi, skillApi } from "../learningApi";

export const learningKeys = {
  skills: (params) => ["learningSkills", params],
  seminars: (filters) => ["seminars", filters],
  seminar: (id) => ["seminar", id],
  record: (employeeId) => ["learningRecord", employeeId],
  checkInQr: (id) => ["seminar", id, "checkInQr"],
  files: (id) => ["seminar", id, "files"],
};

const SKILL_KEYS = [["learningSkills"], ["seminars"], ["seminar"]];
// Participants / attendance change seminar counts and employees' learning records
const SEMINAR_KEYS = [["seminars"], ["seminar"], ["learningRecord"]];

const useLearningMutation = (fn, keys) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () =>
      keys.forEach((queryKey) => qc.invalidateQueries({ queryKey })),
  });
};

/* ---------- Skills ---------- */
export const useSkills = (params = {}) =>
  useQuery({
    queryKey: learningKeys.skills(params),
    queryFn: async () => {
      const res = await skillApi.getAll(params);
      return Array.isArray(res) ? res : res?.data || [];
    },
    staleTime: 60 * 1000,
  });
export const useCreateSkill = () =>
  useLearningMutation((body) => skillApi.create(body), SKILL_KEYS);
export const useUpdateSkill = () =>
  useLearningMutation(
    ({ id, ...body }) => skillApi.update(id, body),
    SKILL_KEYS,
  );
export const useDeleteSkill = () =>
  useLearningMutation((id) => skillApi.remove(id), SKILL_KEYS);

/* ---------- Seminars ---------- */
export const useSeminars = (filters) =>
  useQuery({
    queryKey: learningKeys.seminars(filters),
    queryFn: () => seminarApi.getList(filters),
    placeholderData: keepPreviousData,
  });
export const useSeminar = (id) =>
  useQuery({
    queryKey: learningKeys.seminar(id),
    queryFn: () => seminarApi.getById(id),
    enabled: !!id,
    retry: false,
  });

export const useCreateSeminar = () =>
  useLearningMutation((body) => seminarApi.create(body), SEMINAR_KEYS);
export const useUpdateSeminar = () =>
  useLearningMutation(
    ({ id, ...body }) => seminarApi.update(id, body),
    SEMINAR_KEYS,
  );
export const useCancelSeminar = () =>
  useLearningMutation(
    ({ id, reason }) => seminarApi.cancel(id, reason),
    SEMINAR_KEYS,
  );
export const useCompleteSeminar = () =>
  useLearningMutation((id) => seminarApi.complete(id), SEMINAR_KEYS);
export const useDeleteSeminar = () =>
  useLearningMutation((id) => seminarApi.remove(id), SEMINAR_KEYS);
export const useAssignParticipants = () =>
  useLearningMutation(
    ({ id, ...body }) => seminarApi.assign(id, body),
    SEMINAR_KEYS,
  );
export const useRemoveParticipant = () =>
  useLearningMutation(
    ({ id, employeeId }) => seminarApi.removeParticipant(id, employeeId),
    SEMINAR_KEYS,
  );
export const useSaveSeminarAttendance = () =>
  useLearningMutation(
    ({ id, entries }) => seminarApi.saveAttendance(id, entries),
    SEMINAR_KEYS,
  );

/* ---------- Employee learning record ---------- */
export const useLearningRecord = (employeeId, { enabled = true } = {}) =>
  useQuery({
    queryKey: learningKeys.record(employeeId),
    queryFn: () => seminarApi.getEmployeeRecord(employeeId),
    enabled: !!employeeId && enabled,
    retry: false,
  });

/* ---------- Seminar QR check-in, attendance sheet, files ---------- */
export const useSeminarCheckInQr = (id, { enabled = true } = {}) =>
  useQuery({
    queryKey: learningKeys.checkInQr(id),
    queryFn: () => seminarApi.getCheckInQr(id),
    enabled: !!id && enabled,
  });
export const useEnableCheckInQr = () =>
  useLearningMutation((id) => seminarApi.enableCheckInQr(id), [["seminar"]]);
export const useDisableCheckInQr = () =>
  useLearningMutation((id) => seminarApi.disableCheckInQr(id), [["seminar"]]);
/** Preview (apply=false) changes nothing; apply=true saves attendance like the table does. */
export const useUploadAttendanceSheet = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file, apply }) =>
      seminarApi.uploadAttendanceSheet(id, file, apply),
    onSuccess: (_res, { apply }) =>
      apply &&
      SEMINAR_KEYS.forEach((queryKey) => qc.invalidateQueries({ queryKey })),
  });
};
export const useSeminarFiles = (id) =>
  useQuery({
    queryKey: learningKeys.files(id),
    queryFn: () => seminarApi.getFiles(id),
    enabled: !!id,
  });
export const useUploadSeminarFile = () =>
  useLearningMutation(
    ({ id, ...body }) => seminarApi.uploadFile(id, body),
    [["seminar"]],
  );
export const useDeleteSeminarFile = () =>
  useLearningMutation(
    ({ id, fileId }) => seminarApi.removeFile(id, fileId),
    [["seminar"]],
  );
